import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useMemo } from 'react'
import type { ActivityNode, DependencyLink } from '../../api/network'

const NODE_WIDTH = 220
const NODE_HEIGHT = 108
const COLUMN_GAP = 96
const ROW_GAP = 28

const STATUS_FILL: Record<string, string> = {
  NOT_STARTED: '#f4f6f8',
  IN_PROGRESS: '#e8f1fb',
  COMPLETED: '#e6f6ec',
  DELAYED: '#fdeeed',
  ON_HOLD: '#fdf3e2',
}

interface Placed {
  node: ActivityNode
  x: number
  y: number
}

/**
 * The network, drawn as boxes in dependency order.
 *
 * <p>Columns are the depth of an activity in the network rather than its calendar date, so
 * the chart reads as "what has to happen before what" — which is the question a PERT chart
 * is for. Dates are on the boxes for the other question.</p>
 *
 * <p>Drawn as plain SVG rather than with a graph library: the layout a construction
 * programme needs is a layered left-to-right one, which is a sort and a subtraction, and a
 * dependency that renders arbitrary graphs would be a far larger thing to carry.</p>
 */
export function NetworkDiagram({
  activities,
  dependencies,
  selectedId,
  onSelect,
}: {
  activities: ActivityNode[]
  dependencies: DependencyLink[]
  selectedId?: string
  onSelect: (node: ActivityNode) => void
}) {
  const { placed, width, height } = useMemo(() => {
    const columns = new Map<number, ActivityNode[]>()
    for (const node of activities) {
      const column = columns.get(node.depth) ?? []
      column.push(node)
      columns.set(node.depth, column)
    }

    const positions: Placed[] = []
    let maxRows = 0
    for (const [depth, column] of [...columns.entries()].sort((a, b) => a[0] - b[0])) {
      column.forEach((node, row) => {
        positions.push({
          node,
          x: depth * (NODE_WIDTH + COLUMN_GAP),
          y: row * (NODE_HEIGHT + ROW_GAP),
        })
      })
      maxRows = Math.max(maxRows, column.length)
    }

    const depths = [...columns.keys()]
    const widest = depths.length === 0 ? 0 : Math.max(...depths) + 1
    return {
      placed: positions,
      width: Math.max(widest * (NODE_WIDTH + COLUMN_GAP), NODE_WIDTH) + 8,
      height: Math.max(maxRows * (NODE_HEIGHT + ROW_GAP), NODE_HEIGHT) + 8,
    }
  }, [activities])

  const byId = useMemo(() => {
    const map = new Map<string, Placed>()
    placed.forEach((p) => map.set(p.node.id, p))
    return map
  }, [placed])

  if (activities.length === 0) {
    return (
      <Box sx={{ p: 6, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No activities yet. Add the first one to start the programme.
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ overflow: 'auto', p: 2 }}>
      <svg width={width} height={height} role="img" aria-label="Activity network">
        <defs>
          <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6"
                  orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#8a97a3" />
          </marker>
          <marker id="arrow-critical" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6"
                  markerHeight="6" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#b3261e" />
          </marker>
        </defs>

        {dependencies.map((link) => {
          const from = byId.get(link.predecessorId)
          const to = byId.get(link.successorId)
          if (!from || !to) return null

          const x1 = from.x + NODE_WIDTH
          const y1 = from.y + NODE_HEIGHT / 2
          const x2 = to.x
          const y2 = to.y + NODE_HEIGHT / 2
          const mid = x1 + (x2 - x1) / 2

          return (
            <g key={link.id}>
              <path
                d={`M ${x1} ${y1} C ${mid} ${y1}, ${mid} ${y2}, ${x2} ${y2}`}
                fill="none"
                stroke={link.critical ? '#b3261e' : '#8a97a3'}
                strokeWidth={link.critical ? 2 : 1.25}
                strokeDasharray={link.dependencyType === 'FS' ? undefined : '5 3'}
                markerEnd={link.critical ? 'url(#arrow-critical)' : 'url(#arrow)'}
              />
              {(link.lagDays !== 0 || link.dependencyType !== 'FS') && (
                <text x={mid} y={(y1 + y2) / 2 - 6} textAnchor="middle" fontSize="10" fill="#6b7783">
                  {link.dependencyType}
                  {link.lagDays !== 0 && (link.lagDays > 0 ? ` +${link.lagDays}d` : ` ${link.lagDays}d`)}
                </text>
              )}
            </g>
          )
        })}

        {placed.map(({ node, x, y }) => {
          const selected = node.id === selectedId
          const percent = node.percentComplete ?? 0
          const blocked = node.blockers.length > 0

          return (
            <g
              key={node.id}
              transform={`translate(${x}, ${y})`}
              onClick={() => onSelect(node)}
              style={{ cursor: 'pointer' }}
            >
              <rect
                width={NODE_WIDTH}
                height={NODE_HEIGHT}
                rx={node.milestone ? 18 : 8}
                fill={STATUS_FILL[node.status] ?? '#f4f6f8'}
                stroke={selected ? '#0d4a8a' : node.critical ? '#b3261e' : '#c6ced6'}
                strokeWidth={selected ? 3 : node.critical ? 2 : 1}
              />

              <text x={12} y={22} fontSize="12" fontFamily="monospace" fill="#42525f">
                {node.code}
              </text>
              {node.critical && (
                <text x={NODE_WIDTH - 12} y={22} fontSize="10" textAnchor="end" fill="#b3261e"
                      fontWeight="700">
                  CRITICAL
                </text>
              )}

              <text x={12} y={42} fontSize="13" fontWeight="600" fill="#1f2b36">
                {truncate(node.name, 26)}
              </text>

              <text x={12} y={60} fontSize="11" fill="#6b7783">
                {node.milestone
                  ? formatDate(node.scheduledStart)
                  : `${formatDate(node.scheduledStart)} → ${formatDate(node.scheduledFinish)}`}
              </text>

              <text x={12} y={76} fontSize="10" fill="#6b7783">
                {node.milestone ? 'Milestone' : `${node.durationDays}d`}
                {!node.critical && ` · ${node.totalFloat}d float`}
                {node.percentComplete != null && ` · ${node.percentComplete}%`}
              </text>

              {/* Progress from the BOQ lines this activity covers. */}
              <rect x={12} y={84} width={NODE_WIDTH - 24} height={6} rx={3} fill="#dfe5ea" />
              <rect
                x={12}
                y={84}
                width={((NODE_WIDTH - 24) * Math.min(percent, 100)) / 100}
                height={6}
                rx={3}
                fill={node.behindSchedule ? '#b3261e' : percent >= 100 ? '#0b5d3b' : '#0d4a8a'}
              />

              {blocked && (
                <>
                  <circle cx={NODE_WIDTH - 18} cy={NODE_HEIGHT - 18} r={9} fill="#b3261e" />
                  <text
                    x={NODE_WIDTH - 18}
                    y={NODE_HEIGHT - 14}
                    fontSize="11"
                    fontWeight="700"
                    fill="#fff"
                    textAnchor="middle"
                  >
                    {node.blockers.length}
                  </text>
                </>
              )}
            </g>
          )
        })}
      </svg>
    </Box>
  )
}

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max)}…` : text
}

function formatDate(value?: string): string {
  if (!value) return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString(undefined, { day: '2-digit', month: 'short' })
}
